// src/components/admin/GeoLocation.js
import React, { useState, useEffect } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
// Using local state for tabs instead of URL search params to avoid
// conflicting with the parent attendance component's tab system
import geoService from '../../services/geo.service';
import {
    MapPin, Plus, Search, X, Loader2, Trash2, Edit3, Clock,
    CheckCircle, AlertTriangle, Map, History, Building2, Ruler,
    Wifi, Settings, Shield, ShieldAlert
} from 'lucide-react';

const TABS = [
    { key: 'locations', label: 'Office Locations', icon: Building2 },
    { key: 'networks', label: 'Office Networks', icon: Wifi },
    { key: 'settings', label: 'Settings', icon: Settings },
    { key: 'map', label: 'Live Map', icon: Map },
    { key: 'history', label: 'History', icon: History },
];

const GeoLocation = () => {
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [activeTab, setActiveTab] = useState('locations');

    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingLocation, setEditingLocation] = useState(null);

    useEffect(() => {
        loadLocations();
    }, []);

    const loadLocations = async () => {
        setLoading(true);
        try {
            const res = await geoService.getOfficeLocations();
            if (res.success) setLocations(res.data);
        } catch (err) {
            console.error('Error loading locations:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (tab) => setActiveTab(tab);

    return (
        <div className="space-y-6">
            {/* Tabs */}
            <div className={`rounded-lg shadow ${isDark ? 'bg-gray-800 shadow-gray-900' : 'bg-white'}`}>
                <div className={`border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                    <nav className="tour-geo-tabs flex space-x-8 px-6">
                        {TABS.map(tab => {
                            const Icon = tab.icon;
                            const isActive = activeTab === tab.key;
                            return (
                                <button key={tab.key} onClick={() => handleTabChange(tab.key)}
                                    className={`py-4 px-2 border-b-2 font-medium text-sm transition-colors flex items-center gap-2 ${
                                        isActive
                                            ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                                            : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                                    }`}>
                                    <Icon className="w-4 h-4" />
                                    {tab.label}
                                </button>
                            );
                        })}
                    </nav>
                </div>
            </div>

            {activeTab === 'locations' && (
                <LocationsTab isDark={isDark} locations={locations} loading={loading}
                    onRefresh={loadLocations}
                    onEdit={(loc) => { setEditingLocation(loc); setShowModal(true); }}
                    onAdd={() => { setEditingLocation(null); setShowModal(true); }}
                    onDelete={async (id) => { await geoService.deleteOfficeLocation(id); loadLocations(); }}
                />
            )}
            {activeTab === 'networks' && <OfficeNetworksTab isDark={isDark} locations={locations} />}
            {activeTab === 'settings' && <VerificationSettingsTab isDark={isDark} />}
            {activeTab === 'map' && <LiveMapTab isDark={isDark} offices={locations} />}
            {activeTab === 'history' && <HistoryTab isDark={isDark} />}

            {/* Location Form Modal */}
            {showModal && (
                <LocationFormModal
                    isDark={isDark}
                    location={editingLocation}
                    onClose={() => { setShowModal(false); setEditingLocation(null); }}
                    onSave={async (data) => {
                        if (editingLocation) await geoService.updateOfficeLocation(editingLocation.id, data);
                        else await geoService.createOfficeLocation(data);
                        setShowModal(false);
                        setEditingLocation(null);
                        loadLocations();
                    }}
                />
            )}
        </div>
    );
};

// ─── Locations Tab ───────────────────────────────────────────────────
const LocationsTab = ({ isDark, locations, loading, onRefresh, onEdit, onAdd, onDelete }) => (
    <div className="space-y-4">
        <div className="flex items-center justify-between">
            <div>
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Office Locations</h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Define geofence zones for attendance verification. {locations.length} location{locations.length !== 1 ? 's' : ''} configured.
                </p>
            </div>
            <button onClick={onAdd}
                className="tour-geo-add-location flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                <Plus className="w-4 h-4" /> Add Location
            </button>
        </div>

        {loading ? (
            <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
        ) : locations.length === 0 ? (
            <div className={`rounded-xl p-12 text-center border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <MapPin className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>No office locations configured</h3>
                <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Add your first office location to enable geofence-based attendance verification.
                </p>
                <button onClick={onAdd} className="mt-4 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg">Add Location</button>
            </div>
        ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {locations.map(loc => (
                    <div key={loc.id} className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: loc.color || '#6366f1' }} />
                                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{loc.name}</h3>
                            </div>
                            <div className="flex gap-1">
                                <button onClick={() => onEdit(loc)} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                    <Edit3 className="w-3.5 h-3.5 text-gray-500" />
                                </button>
                                <button onClick={() => onDelete(loc.id)} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20">
                                    <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                </button>
                            </div>
                        </div>
                        {loc.address && <p className={`text-xs mb-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{loc.address}</p>}
                        <div className={`flex items-center gap-4 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{Number(loc.latitude).toFixed(4)}, {Number(loc.longitude).toFixed(4)}</span>
                            <span className="flex items-center gap-1"><Ruler className="w-3 h-3" />{loc.radiusMeters}m radius</span>
                        </div>
                        <div className="mt-2">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium ${
                                loc.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                            }`}>
                                {loc.isActive ? 'Active' : 'Inactive'}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        )}
    </div>
);

// ─── Live Map Tab ────────────────────────────────────────────────────
const LiveMapTab = ({ isDark, offices }) => {
    const [punchData, setPunchData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedPunch, setSelectedPunch] = useState(null);
    const [mapReady, setMapReady] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);

    useEffect(() => {
        Promise.all([import('react-leaflet'), import('leaflet')]).then(() => {
            setMapReady(true);
        }).catch(() => setMapReady(false));

        loadPunchData();
    }, []);

    const loadPunchData = async (isRefresh = false) => {
        if (isRefresh) setRefreshing(true);
        try {
            const res = await geoService.getPunchMap();
            if (res.success) { setPunchData(res.data); setLastUpdated(new Date()); }
        } catch (err) { console.error(err); }
        finally { setLoading(false); setRefreshing(false); }
    };

    if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

    if (!mapReady) {
        return (
            <div className={`rounded-xl p-12 text-center border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <Map className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Live Map</h3>
                <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Map view showing today's employee punch locations overlaid on office geofences.
                </p>
                <p className={`text-xs mt-4 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                    {punchData?.punches?.length || 0} punch records today across {punchData?.offices?.length || 0} office locations.
                </p>

                {/* Punch list fallback when map isn't available */}
                {punchData?.punches?.length > 0 && (
                    <div className="mt-6 text-left max-h-64 overflow-y-auto">
                        {punchData.punches.map((p, i) => (
                            <div key={i} className={`flex items-center justify-between px-3 py-2 ${i > 0 ? `border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}` : ''}`}>
                                <div>
                                    <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{p.employeeName}</p>
                                    <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                        {p.punchType === 'check_in' ? 'Check In' : 'Check Out'} · {new Date(p.punchTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                    </p>
                                </div>
                                <div className="flex items-center gap-2">
                                    {p.geofenceStatus?.violation && (
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Outside</span>
                                    )}
                                    <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                                        {p.locationType || '—'}
                                    </span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    // Map is available - render with Leaflet
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Today's Punch Locations</h2>
                <div className="flex items-center gap-3">
                    {lastUpdated && (
                        <span className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Updated {lastUpdated.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                    )}
                    <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        {(() => {
                            const total = punchData?.punches?.length || 0;
                            const withGps = punchData?.punches?.filter(p => p.geoLocation?.latitude).length || 0;
                            return withGps === total ? `${total} punches` : `${withGps}/${total} with GPS`;
                        })()}
                    </span>
                    <button onClick={() => loadPunchData(true)} disabled={refreshing}
                        className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`} title="Refresh">
                        <Clock className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''} ${isDark ? 'text-gray-400' : 'text-gray-500'}`} />
                    </button>
                </div>
            </div>
            <LeafletMapView isDark={isDark} punchData={punchData} offices={offices} />
        </div>
    );
};

// Leaflet Map Component (loaded dynamically)
const LeafletMapView = ({ isDark, punchData, offices }) => {
    const [L, setL] = React.useState(null);
    const [RL, setRL] = React.useState(null);

    useEffect(() => {
        // Load Leaflet CSS (required for correct map rendering)
        if (!document.getElementById('leaflet-css')) {
            const link = document.createElement('link');
            link.id = 'leaflet-css';
            link.rel = 'stylesheet';
            link.href = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
            document.head.appendChild(link);
        }

        import('leaflet').then(mod => {
            // Fix default marker icon issue
            delete mod.default.Icon.Default.prototype._getIconUrl;
            mod.default.Icon.Default.mergeOptions({
                iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
                iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
                shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
            });
            setL(mod.default);
        });
        import('react-leaflet').then(setRL);
    }, []);

    if (!L || !RL) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

    const { MapContainer, TileLayer, Circle, Marker, Popup, Tooltip } = RL;

    // Default center: first office or first punch location or India center
    const allOffices = punchData?.offices?.length ? punchData.offices : offices;
    const firstPunch = punchData?.punches?.find(p => p.geoLocation?.latitude);
    const center = allOffices.length > 0
        ? [Number(allOffices[0].latitude), Number(allOffices[0].longitude)]
        : firstPunch
            ? [Number(firstPunch.geoLocation.latitude), Number(firstPunch.geoLocation.longitude)]
            : [20.5937, 78.9629];

    return (
        <div className={`rounded-xl overflow-hidden border ${isDark ? 'border-gray-700' : 'border-gray-200'}`} style={{ height: '500px' }}>
            <MapContainer center={center} zoom={13} style={{ height: '100%', width: '100%' }}>
                    <TileLayer
                        attribution='&copy; OpenStreetMap contributors'
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    {/* Office geofence circles */}
                    {(punchData?.offices || offices).map(office => {
                        const radius = office.radiusMeters || office.radius_meters || 200;
                        return (
                            <Circle
                                key={office.id}
                                center={[Number(office.latitude), Number(office.longitude)]}
                                radius={radius}
                                pathOptions={{ color: office.color || '#6366f1', fillColor: office.color || '#6366f1', fillOpacity: 0.1, weight: 2 }}
                            >
                                <Tooltip permanent>{office.name} ({radius}m)</Tooltip>
                            </Circle>
                        );
                    })}
                    {/* Punch markers */}
                    {punchData?.punches?.filter(p => p.geoLocation?.latitude).map((p, i) => (
                        <Marker key={i} position={[Number(p.geoLocation.latitude), Number(p.geoLocation.longitude)]}>
                            <Popup>
                                <div className="text-sm">
                                    <p className="font-semibold">{p.employeeName}</p>
                                    <p className="text-gray-500">
                                        {p.punchType === 'check_in' ? 'Check In' : 'Check Out'} · {new Date(p.punchTime).toLocaleTimeString('en-IN')}
                                    </p>
                                    {p.geofenceStatus?.withinGeofence === false && (
                                        <p className="text-red-500 text-xs">Outside geofence ({p.geofenceStatus.distanceMeters}m)</p>
                                    )}
                                </div>
                            </Popup>
                        </Marker>
                    ))}
                </MapContainer>
        </div>
    );
};

// ─── History Tab ─────────────────────────────────────────────────────
const HistoryTab = ({ isDark }) => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');

    useEffect(() => {
        loadHistory();
    }, [page, dateFrom, dateTo]);

    const loadHistory = async () => {
        setLoading(true);
        try {
            const params = { page, limit: 25 };
            if (dateFrom) params.dateFrom = dateFrom;
            if (dateTo) params.dateTo = dateTo;
            const res = await geoService.getPunchHistory(params);
            if (res.success) { setHistory(res.data); setTotal(res.total); }
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap gap-3 items-center">
                <div className="flex items-center gap-2">
                    <label className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>From:</label>
                    <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPage(1); }}
                        className={`px-3 py-1.5 rounded-lg text-sm ${isDark ? 'bg-gray-800 border border-gray-700 text-white' : 'bg-white border border-gray-300 text-gray-900'}`} />
                </div>
                <div className="flex items-center gap-2">
                    <label className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>To:</label>
                    <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPage(1); }}
                        className={`px-3 py-1.5 rounded-lg text-sm ${isDark ? 'bg-gray-800 border border-gray-700 text-white' : 'bg-white border border-gray-300 text-gray-900'}`} />
                </div>
                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{total} records</span>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
            ) : history.length === 0 ? (
                <div className={`rounded-xl p-12 text-center border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <History className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>No location history</h3>
                    <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Location history will appear here as employees check in with location enabled.
                    </p>
                </div>
            ) : (
                <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead>
                                <tr className={isDark ? 'bg-gray-700' : 'bg-gray-50'}>
                                    {['Employee', 'Date', 'Time', 'Type', 'Location', 'Distance', 'Status'].map(h => (
                                        <th key={h} className={`px-4 py-3 text-left text-xs font-semibold uppercase ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{h}</th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((h, i) => (
                                    <tr key={i} className={`border-t ${isDark ? 'border-gray-700' : 'border-gray-100'}`}>
                                        <td className={`px-4 py-3 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{h.employeeName}</td>
                                        <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {new Date(h.punchDate).toLocaleDateString('en-IN')}
                                        </td>
                                        <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {new Date(h.punchTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className={`text-xs px-2 py-0.5 rounded-full ${
                                                h.punchType === 'check_in' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                                            }`}>
                                                {h.punchType === 'check_in' ? 'Check In' : 'Check Out'}
                                            </span>
                                        </td>
                                        <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {h.geofenceStatus?.locationName || h.locationType || '—'}
                                        </td>
                                        <td className={`px-4 py-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            {h.geofenceStatus?.distanceMeters != null ? `${h.geofenceStatus.distanceMeters}m` : '—'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            {h.geofenceStatus?.unreliableSource ? (
                                                <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap inline-flex items-center gap-1 ${isDark ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                                                    <AlertTriangle className="w-3 h-3" /> IP only
                                                </span>
                                            ) : h.geofenceStatus?.officesConfigured === false ? (
                                                <span className={`text-xs whitespace-nowrap ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No offices</span>
                                            ) : h.geofenceStatus?.withinGeofence === false ? (
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 inline-flex items-center gap-1 whitespace-nowrap">
                                                    <AlertTriangle className="w-3 h-3" /> Outside
                                                </span>
                                            ) : h.geofenceStatus?.withinGeofence === true ? (
                                                <span className="text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 inline-flex items-center gap-1 whitespace-nowrap">
                                                    <CheckCircle className="w-3 h-3" /> Inside
                                                </span>
                                            ) : !h.geofenceStatus && h.geoLocation?.latitude ? (
                                                <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${isDark ? 'bg-yellow-900/30 text-yellow-400' : 'bg-yellow-100 text-yellow-700'}`}>
                                                    Not verified
                                                </span>
                                            ) : (
                                                <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-500'}`}>
                                                    No GPS
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {/* Pagination */}
                    {total > 25 && (
                        <div className={`flex items-center justify-between px-4 py-3 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                                Showing {((page - 1) * 25) + 1}–{Math.min(page * 25, total)} of {total}
                            </span>
                            <div className="flex gap-2">
                                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                                    className={`px-3 py-1 rounded text-sm ${isDark ? 'bg-gray-700 text-gray-300 disabled:opacity-50' : 'bg-gray-100 text-gray-700 disabled:opacity-50'}`}>Prev</button>
                                <button onClick={() => setPage(p => p + 1)} disabled={page * 25 >= total}
                                    className={`px-3 py-1 rounded text-sm ${isDark ? 'bg-gray-700 text-gray-300 disabled:opacity-50' : 'bg-gray-100 text-gray-700 disabled:opacity-50'}`}>Next</button>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// ─── Location Form Modal ─────────────────────────────────────────────
const LocationFormModal = ({ isDark, location, onClose, onSave }) => {
    const [form, setForm] = useState({
        name: location?.name || '',
        address: location?.address || '',
        latitude: location?.latitude ?? '',
        longitude: location?.longitude ?? '',
        radiusMeters: location?.radiusMeters ?? 200,
        color: location?.color || '#6366f1',
        isActive: location?.isActive !== false,
    });
    const [saving, setSaving] = useState(false);
    const [detecting, setDetecting] = useState(false);
    const [showMapPicker, setShowMapPicker] = useState(false);
    const [geoError, setGeoError] = useState('');
    const [geoAccuracy, setGeoAccuracy] = useState(null);
    const watchRef = React.useRef(null);

    // Address autocomplete state
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [addressLoading, setAddressLoading] = useState(false);
    const addressDebounceRef = React.useRef(null);
    const addressWrapperRef = React.useRef(null);

    // Close suggestions on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (addressWrapperRef.current && !addressWrapperRef.current.contains(e.target)) {
                setShowSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Clean up watchPosition on unmount
    useEffect(() => {
        return () => {
            if (watchRef.current !== null) {
                navigator.geolocation.clearWatch(watchRef.current);
            }
            if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);
        };
    }, []);

    // Fetch address suggestions from Nominatim (debounced)
    const handleAddressChange = (value) => {
        setForm(f => ({ ...f, address: value }));
        if (addressDebounceRef.current) clearTimeout(addressDebounceRef.current);

        if (value.trim().length < 3) {
            setAddressSuggestions([]);
            setShowSuggestions(false);
            return;
        }

        addressDebounceRef.current = setTimeout(async () => {
            setAddressLoading(true);
            try {
                const res = await fetch(
                    `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(value)}&limit=6&addressdetails=1&dedupe=1`,
                    { headers: { 'Accept-Language': 'en' } }
                );
                if (res.ok) {
                    const data = await res.json();
                    setAddressSuggestions(data);
                    setShowSuggestions(data.length > 0);
                }
            } catch (err) {
                console.error('Address search failed:', err);
            } finally {
                setAddressLoading(false);
            }
        }, 400);
    };

    const selectAddress = (suggestion) => {
        setForm(f => ({
            ...f,
            address: suggestion.display_name,
            latitude: parseFloat(suggestion.lat),
            longitude: parseFloat(suggestion.lon),
        }));
        setShowSuggestions(false);
        setAddressSuggestions([]);
        setGeoAccuracy(null);
        setGeoError('');
    };

    // Reverse geocode: fill address from coordinates
    const reverseGeocode = async (lat, lng) => {
        try {
            const res = await fetch(
                `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`,
                { headers: { 'Accept-Language': 'en' } }
            );
            if (res.ok) {
                const data = await res.json();
                if (data.display_name) {
                    setForm(f => ({ ...f, address: data.display_name }));
                }
            }
        } catch (e) { /* silently fail — address is optional */ }
    };

    // IP-based geolocation fallback (works on all devices including desktops)
    const detectViaIP = async () => {
        try {
            const res = await fetch('https://ipapi.co/json/', { signal: AbortSignal.timeout(8000) });
            if (!res.ok) throw new Error('IP lookup failed');
            const data = await res.json();
            if (data.latitude && data.longitude) {
                return { latitude: data.latitude, longitude: data.longitude, source: 'ip', city: data.city };
            }
        } catch (e) { /* silently fail */ }
        return null;
    };

    const detectLocation = async () => {
        setGeoError('');
        setGeoAccuracy(null);

        // Stop any previous watch
        if (watchRef.current !== null) {
            navigator.geolocation.clearWatch(watchRef.current);
            watchRef.current = null;
        }

        setDetecting(true);

        // If browser geolocation is not available at all, go straight to IP
        if (!navigator.geolocation) {
            const ipResult = await detectViaIP();
            setDetecting(false);
            if (ipResult) {
                setForm(f => ({ ...f, latitude: ipResult.latitude, longitude: ipResult.longitude }));
                reverseGeocode(ipResult.latitude, ipResult.longitude);
                setGeoAccuracy(null);
                setGeoError('Location estimated from IP address' + (ipResult.city ? ` (near ${ipResult.city})` : '') + '. Use the map picker to set the exact office position.');
            } else {
                setGeoError('Could not detect location. Use the map picker to select your office location.');
            }
            return;
        }

        // Try browser geolocation with watchPosition for accuracy refinement
        let bestAccuracy = Infinity;
        let settled = false;

        const stopWatch = () => {
            if (watchRef.current !== null) {
                navigator.geolocation.clearWatch(watchRef.current);
                watchRef.current = null;
            }
        };

        const onSuccess = (pos) => {
            const { latitude, longitude, accuracy } = pos.coords;
            if (accuracy < bestAccuracy) {
                bestAccuracy = accuracy;
                setForm(f => ({ ...f, latitude, longitude }));
                setGeoAccuracy(Math.round(accuracy));
            }
            if (!settled && (accuracy < 100 || bestAccuracy < Infinity)) {
                settled = true;
                reverseGeocode(latitude, longitude);
                setTimeout(() => {
                    stopWatch();
                    setDetecting(false);
                    if (bestAccuracy > 500) {
                        setGeoError('Location detected but accuracy is low (~' + Math.round(bestAccuracy) + 'm). Use the map picker for precise placement.');
                    }
                }, 5000);
            }
        };

        const onError = async (error) => {
            stopWatch();
            // If we already got at least one reading, keep it
            if (bestAccuracy < Infinity) {
                setDetecting(false);
                setGeoError('Accuracy is approximate (~' + Math.round(bestAccuracy) + 'm). Refine using the map picker if needed.');
                return;
            }

            // Browser geolocation failed entirely — try IP-based fallback
            const ipResult = await detectViaIP();
            if (ipResult) {
                setForm(f => ({ ...f, latitude: ipResult.latitude, longitude: ipResult.longitude }));
                reverseGeocode(ipResult.latitude, ipResult.longitude);
                setGeoAccuracy(null);
                setDetecting(false);
                const hint = error.code === 1
                    ? 'Browser location was denied — estimated from IP'
                    : 'Browser location unavailable — estimated from IP';
                setGeoError(hint + (ipResult.city ? ` (near ${ipResult.city})` : '') + '. Use the map picker to set the exact position.');
                return;
            }

            // Everything failed (browser + IP)
            setDetecting(false);
            if (error.code === 1) {
                setGeoError('Location permission denied by browser. Allow location access and try again, or use the map picker.');
            } else {
                setGeoError('Could not detect location automatically. Desktop browsers often lack GPS — use the map picker or manually enter coordinates.');
            }
        };

        // Start high-accuracy watch
        watchRef.current = navigator.geolocation.watchPosition(onSuccess, onError, {
            enableHighAccuracy: true,
            timeout: 10000,
            maximumAge: 0,
        });

        // Safety timeout — stop after 15 seconds and try IP fallback if nothing
        setTimeout(async () => {
            if (watchRef.current !== null) {
                stopWatch();
                if (bestAccuracy < Infinity) {
                    setDetecting(false);
                    return;
                }
                // Still nothing — try IP
                const ipResult = await detectViaIP();
                if (ipResult) {
                    setForm(f => ({ ...f, latitude: ipResult.latitude, longitude: ipResult.longitude }));
                    reverseGeocode(ipResult.latitude, ipResult.longitude);
                    setGeoAccuracy(null);
                    setGeoError('Browser location timed out — estimated from IP' + (ipResult.city ? ` (near ${ipResult.city})` : '') + '. Use the map picker for exact coordinates.');
                } else {
                    setGeoError('Could not detect location. Use the map picker to select your office location.');
                }
                setDetecting(false);
            }
        }, 15000);
    };

    const handleMapClick = (e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        // Map displays roughly India center if no coords, otherwise centered on existing coords
        // We use OpenStreetMap iframe with click-to-pick via a simple approach
    };

    const [saveError, setSaveError] = useState('');

    const handleSave = async () => {
        if (!form.name.trim() || form.latitude === '' || form.longitude === '') return;
        setSaving(true);
        setSaveError('');
        try {
            await onSave({
                ...form,
                latitude: Number(form.latitude),
                longitude: Number(form.longitude),
                radiusMeters: Number(form.radiusMeters),
            });
        } catch (err) {
            setSaveError(err.response?.data?.error || 'Failed to save location');
        } finally { setSaving(false); }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className={`w-full max-w-lg rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} p-6 max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
                {saveError && (
                    <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
                        {saveError}
                    </div>
                )}
                <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{location ? 'Edit Location' : 'Add Office Location'}</h3>
                    <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
                </div>
                <div className="space-y-3">
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Name *</label>
                        <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                            className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                            placeholder="e.g., Main Office, Branch Office" />
                    </div>
                    <div ref={addressWrapperRef} className="relative">
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Address</label>
                        <div className="relative">
                            <input value={form.address}
                                onChange={e => handleAddressChange(e.target.value)}
                                onFocus={() => { if (addressSuggestions.length > 0) setShowSuggestions(true); }}
                                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`}
                                placeholder="Start typing to search addresses..."
                                autoComplete="off" />
                            {addressLoading && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />
                                </div>
                            )}
                        </div>
                        {showSuggestions && addressSuggestions.length > 0 && (
                            <div className={`absolute z-10 w-full mt-1 rounded-lg border shadow-lg max-h-48 overflow-y-auto ${isDark ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'}`}>
                                {addressSuggestions.map((s, i) => (
                                    <button key={s.place_id || i} type="button"
                                        onClick={() => selectAddress(s)}
                                        className={`w-full text-left px-3 py-2 text-sm flex items-start gap-2 transition-colors ${
                                            isDark ? 'hover:bg-gray-600 text-gray-200' : 'hover:bg-gray-50 text-gray-700'
                                        } ${i > 0 ? `border-t ${isDark ? 'border-gray-600' : 'border-gray-100'}` : ''}`}>
                                        <MapPin className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                        <span className="line-clamp-2">{s.display_name}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Map Picker */}
                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <label className={`text-xs font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                                Coordinates * <span className="text-gray-400 font-normal">(click map to set)</span>
                            </label>
                            <button onClick={() => setShowMapPicker(!showMapPicker)} type="button"
                                className={`text-xs font-medium px-2 py-1 rounded flex items-center gap-1 ${showMapPicker ? 'bg-blue-600 text-white' : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                                <Map className="w-3 h-3" /> {showMapPicker ? 'Hide Map' : 'Show Map'}
                            </button>
                        </div>
                        {showMapPicker && (
                            <div className="mb-2 rounded-lg overflow-hidden border" style={{ height: '220px' }}>
                                <iframe
                                    title="Pick Location"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${Number(form.longitude || 78.4) - 0.02},${Number(form.latitude || 20.5) - 0.01},${Number(form.longitude || 78.4) + 0.02},${Number(form.latitude || 20.5) + 0.01}&layer=mapnik&marker=${form.latitude || 20.5},${form.longitude || 78.4}`}
                                />
                                <div className={`text-[10px] px-2 py-1 ${isDark ? 'bg-gray-700 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                                    Navigate to your office, right-click the exact spot → copy coordinates, then paste below. Or{' '}
                                    <a href="https://www.google.com/maps" target="_blank" rel="noreferrer" className="text-blue-500 underline">open Google Maps</a> for a bigger view.
                                </div>
                            </div>
                        )}
                        <div className="grid grid-cols-2 gap-3">
                            <input type="number" step="any" value={form.latitude} onChange={e => setForm({...form, latitude: e.target.value})}
                                placeholder="Latitude"
                                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`} />
                            <input type="number" step="any" value={form.longitude} onChange={e => setForm({...form, longitude: e.target.value})}
                                placeholder="Longitude"
                                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`} />
                        </div>
                    </div>

                    <div className="flex gap-2">
                        <button onClick={detectLocation} disabled={detecting}
                            className={`flex-1 py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} ${detecting ? 'opacity-75' : ''}`}>
                            {detecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <MapPin className="w-4 h-4" />}
                            {detecting ? 'Detecting location...' : 'Auto-detect Location'}
                        </button>
                    </div>
                    {detecting && geoAccuracy && (
                        <div className={`flex items-center gap-2 text-xs ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                            <Loader2 className="w-3 h-3 animate-spin" />
                            Refining accuracy: ~{geoAccuracy}m
                        </div>
                    )}
                    {!detecting && geoAccuracy && !geoError && (
                        <p className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                            <CheckCircle className="w-3 h-3 shrink-0" /> Location detected (accuracy: ~{geoAccuracy}m)
                        </p>
                    )}
                    {geoError && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
                            <AlertTriangle className="w-3 h-3 shrink-0" /> {geoError}
                        </p>
                    )}
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                            Geofence Radius: {form.radiusMeters}m
                        </label>
                        <input type="range" min="50" max="2000" step="50" value={form.radiusMeters}
                            onChange={e => setForm({...form, radiusMeters: Number(e.target.value)})}
                            className="w-full accent-blue-600" />
                        <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                            <span>50m</span><span>1000m</span><span>2000m</span>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Color</label>
                            <input type="color" value={form.color} onChange={e => setForm({...form, color: e.target.value})} className="w-full h-9 rounded-lg cursor-pointer" />
                        </div>
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Active</label>
                            <button onClick={() => setForm({...form, isActive: !form.isActive})}
                                className={`w-full py-2 rounded-lg text-sm font-medium ${
                                    form.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                                }`}>
                                {form.isActive ? 'Active' : 'Inactive'}
                            </button>
                        </div>
                    </div>
                </div>
                <div className="flex gap-3 mt-4 justify-end">
                    <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
                    <button onClick={handleSave} disabled={saving || !form.name.trim() || form.latitude === ''}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Office Networks Tab ──────────────────────────────────────────────
const OfficeNetworksTab = ({ isDark, locations }) => {
    const [networks, setNetworks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);

    useEffect(() => { loadNetworks(); }, []);

    const loadNetworks = async () => {
        setLoading(true);
        try {
            const res = await geoService.getOfficeNetworks();
            if (res.success) setNetworks(res.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleSave = async (data) => {
        if (editing) await geoService.updateOfficeNetwork(editing.id, data);
        else await geoService.createOfficeNetwork(data);
        setShowForm(false);
        setEditing(null);
        loadNetworks();
    };

    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <div>
                    <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Office Networks</h2>
                    <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Verify desktop attendance by detecting your office network.
                    </p>
                </div>
                <button onClick={() => { setEditing(null); setShowForm(true); }}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors">
                    <Plus className="w-4 h-4" /> Add Network
                </button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>
            ) : networks.length === 0 ? (
                <div className={`rounded-xl p-12 text-center border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                    <Wifi className={`w-12 h-12 mx-auto mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`} />
                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>No office networks configured</h3>
                    <p className={`text-sm mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Add your office network to verify desktop attendance. Click "Add Network" and use "Detect My Office Network" while connected to your office WiFi.
                    </p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {networks.map(n => (
                        <div key={n.id} className={`rounded-xl border p-4 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                            <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                    <Wifi className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                                    <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>{n.network_name}</h3>
                                </div>
                                <div className="flex gap-1">
                                    <button onClick={() => { setEditing(n); setShowForm(true); }} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700">
                                        <Edit3 className="w-3.5 h-3.5 text-gray-500" />
                                    </button>
                                    <button onClick={async () => { await geoService.deleteOfficeNetwork(n.id); loadNetworks(); }} className="p-1 rounded hover:bg-red-50 dark:hover:bg-red-900/20">
                                        <Trash2 className="w-3.5 h-3.5 text-red-500" />
                                    </button>
                                </div>
                            </div>
                            <p className={`text-sm font-mono ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>
                                {(() => {
                                    const [ip, prefix] = (n.ip_range || '').split('/');
                                    if (prefix === '32') return `IP: ${ip} (exact)`;
                                    if (prefix === '24') return `Subnet: ${ip.split('.').slice(0, 3).join('.')}.*`;
                                    return n.ip_range;
                                })()}
                            </p>
                            {n.description && <p className={`text-xs mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>{n.description}</p>}
                            <div className="flex items-center gap-2 mt-2">
                                {n.location_name && (
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                                        {n.location_name}
                                    </span>
                                )}
                                {n.is_vpn && (
                                    <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">VPN</span>
                                )}
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                    n.is_active ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                                }`}>
                                    {n.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showForm && (
                <NetworkFormModal isDark={isDark} network={editing} locations={locations}
                    onClose={() => { setShowForm(false); setEditing(null); }}
                    onSave={handleSave}
                />
            )}
        </div>
    );
};

// ─── Helper: parse existing CIDR into ip + match type ───────────────
const parseCidr = (cidr) => {
    if (!cidr) return { ipAddress: '', matchType: 'subnet', customPrefix: '24' };
    const [ip, prefix] = cidr.split('/');
    if (prefix === '32') return { ipAddress: ip, matchType: 'exact', customPrefix: '32' };
    if (prefix === '24') return { ipAddress: ip, matchType: 'subnet', customPrefix: '24' };
    return { ipAddress: ip, matchType: 'custom', customPrefix: prefix || '24' };
};

// ─── Helper: compute human-readable IP range summary ────────────────
const getIpRangeSummary = (ipAddress, matchType, customPrefix) => {
    if (!ipAddress || !/^(\d{1,3}\.){3}\d{1,3}$/.test(ipAddress)) return null;
    const prefix = matchType === 'exact' ? 32 : matchType === 'subnet' ? 24 : parseInt(customPrefix);
    if (prefix === 32) return `Will match only ${ipAddress}`;
    if (prefix === 24) {
        const base = ipAddress.split('.').slice(0, 3).join('.');
        return `Will match all IPs from ${base}.0 to ${base}.255`;
    }
    if (prefix >= 0 && prefix <= 32) {
        const ipInt = ipAddress.split('.').reduce((acc, oct) => (acc << 8) + parseInt(oct), 0) >>> 0;
        const mask = prefix === 0 ? 0 : (~0 << (32 - prefix)) >>> 0;
        const start = ipInt & mask;
        const end = start | (~mask >>> 0);
        const toIp = (n) => [(n >>> 24) & 255, (n >>> 16) & 255, (n >>> 8) & 255, n & 255].join('.');
        return `Will match IPs from ${toIp(start)} to ${toIp(end)}`;
    }
    return null;
};

// ─── Network Form Modal ──────────────────────────────────────────────
const NetworkFormModal = ({ isDark, network, locations, onClose, onSave }) => {
    const parsed = parseCidr(network?.ip_range);
    const [form, setForm] = useState({
        name: network?.network_name || '',
        ipAddress: parsed.ipAddress,
        matchType: parsed.matchType,
        customPrefix: parsed.customPrefix,
        description: network?.description || '',
        officeLocationId: network?.office_location_id || '',
        isActive: network?.is_active !== false,
        isVpn: network?.is_vpn || false,
    });
    const [saving, setSaving] = useState(false);
    const [detecting, setDetecting] = useState(false);
    const [detectResult, setDetectResult] = useState(null);
    const [detectError, setDetectError] = useState('');
    const [error, setError] = useState('');

    const isValidIp = (ip) => {
        if (!/^(\d{1,3}\.){3}\d{1,3}$/.test(ip)) return false;
        return ip.split('.').every(o => parseInt(o) >= 0 && parseInt(o) <= 255);
    };

    const handleIpChange = (value) => {
        // Smart paste: if user pastes CIDR like 10.0.0.0/16, auto-split
        if (value.includes('/')) {
            const [ip, prefix] = value.split('/');
            const prefixNum = parseInt(prefix);
            setForm({
                ...form,
                ipAddress: ip,
                matchType: prefixNum === 32 ? 'exact' : prefixNum === 24 ? 'subnet' : 'custom',
                customPrefix: prefix
            });
        } else {
            setForm({ ...form, ipAddress: value });
        }
    };

    const handleDetect = async () => {
        setDetecting(true);
        setDetectError('');
        try {
            const res = await geoService.detectMyIp();
            if (res.success && res.data) {
                const { detectedIp, isIpv6 } = res.data;
                if (isIpv6) {
                    setDetectError('IPv6 address detected. Please enter your office IPv4 address manually.');
                    setDetecting(false);
                    return;
                }
                setDetectResult(detectedIp);
                const octets = detectedIp.split('.');
                setForm({
                    ...form,
                    ipAddress: `${octets[0]}.${octets[1]}.${octets[2]}.0`,
                    matchType: 'subnet',
                    name: form.name || 'Office Network'
                });
            }
        } catch (err) {
            setDetectError('Could not detect your IP. Please enter it manually below.');
        } finally { setDetecting(false); }
    };

    const handleSave = async () => {
        setError('');
        if (!form.name.trim()) { setError('Network name is required.'); return; }
        if (!form.ipAddress.trim() || !isValidIp(form.ipAddress)) {
            setError('Please enter a valid IP address (e.g., 203.0.113.45).');
            return;
        }
        const prefix = form.matchType === 'exact' ? 32 : form.matchType === 'subnet' ? 24 : parseInt(form.customPrefix);
        if (isNaN(prefix) || prefix < 0 || prefix > 32) { setError('Prefix length must be between 0 and 32.'); return; }

        const cidr = `${form.ipAddress}/${prefix}`;
        setSaving(true);
        try {
            await onSave({
                name: form.name,
                ipRange: cidr,
                description: form.description,
                officeLocationId: form.officeLocationId || null,
                isActive: form.isActive,
                isVpn: form.isVpn,
            });
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to save network');
        } finally { setSaving(false); }
    };

    const summary = getIpRangeSummary(form.ipAddress, form.matchType, form.customPrefix);
    const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'} focus:outline-none focus:ring-2 focus:ring-blue-500/20`;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
            <div className={`w-full max-w-md rounded-2xl shadow-2xl ${isDark ? 'bg-gray-800' : 'bg-white'} p-6 max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-4">
                    <h3 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>{network ? 'Edit Network' : 'Add Office Network'}</h3>
                    <button onClick={onClose}><X className="w-5 h-5 text-gray-400" /></button>
                </div>

                {/* Detect My Office Network button */}
                {!network && (
                    <div className={`mb-4 p-3 rounded-xl border-2 border-dashed ${isDark ? 'border-blue-700 bg-blue-900/10' : 'border-blue-200 bg-blue-50'}`}>
                        <button onClick={handleDetect} disabled={detecting}
                            className={`w-full flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                                detecting ? 'bg-blue-400 cursor-wait' : 'bg-blue-600 hover:bg-blue-700'
                            } text-white`}>
                            {detecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />}
                            {detecting ? 'Detecting...' : 'Detect My Office Network'}
                        </button>
                        <p className={`text-[10px] text-center mt-1.5 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
                            Click while connected to your office WiFi. Make sure you're not on a VPN.
                        </p>
                        {detectError && (
                            <div className={`mt-2 px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${isDark ? 'bg-red-900/20 text-red-400' : 'bg-red-50 text-red-700'}`}>
                                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>{detectError}</span>
                            </div>
                        )}
                        {detectResult && (
                            <div className={`mt-2 px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${isDark ? 'bg-green-900/20 text-green-400' : 'bg-green-50 text-green-700'}`}>
                                <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                                <span>Detected IP: <span className="font-mono font-medium">{detectResult}</span></span>
                            </div>
                        )}
                    </div>
                )}

                <div className="space-y-3">
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Network Name *</label>
                        <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                            className={inputCls} placeholder="e.g., Corporate LAN" />
                    </div>
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>IP Address *</label>
                        <input value={form.ipAddress} onChange={e => handleIpChange(e.target.value)}
                            className={`${inputCls} font-mono`} placeholder="e.g., 203.0.113.45" />
                    </div>
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Match Type</label>
                        <div className="grid grid-cols-3 gap-1">
                            {[
                                { key: 'exact', label: 'Exact IP' },
                                { key: 'subnet', label: 'Office Subnet' },
                                { key: 'custom', label: 'Custom Range' }
                            ].map(opt => (
                                <button key={opt.key} onClick={() => setForm({ ...form, matchType: opt.key })}
                                    className={`py-1.5 rounded-lg text-xs font-medium transition-colors ${
                                        form.matchType === opt.key
                                            ? 'bg-blue-600 text-white'
                                            : isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                    }`}>
                                    {opt.label}
                                </button>
                            ))}
                        </div>
                        {form.matchType === 'custom' && (
                            <div className="mt-2 flex items-center gap-2">
                                <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>Prefix length:</span>
                                <input value={form.customPrefix}
                                    onChange={e => setForm({ ...form, customPrefix: e.target.value })}
                                    className={`w-16 px-2 py-1 rounded border text-xs font-mono ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
                                    placeholder="24" type="number" min="0" max="32" />
                            </div>
                        )}
                    </div>
                    {/* Human-readable summary */}
                    {summary && (
                        <div className={`px-3 py-2 rounded-lg text-xs ${isDark ? 'bg-gray-700/50 text-gray-300' : 'bg-gray-50 text-gray-600'}`}>
                            {summary}
                        </div>
                    )}
                    <div>
                        <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Description</label>
                        <input value={form.description} onChange={e => setForm({...form, description: e.target.value})}
                            className={inputCls} placeholder="e.g., 4th floor WiFi" />
                    </div>
                    {locations.length > 0 && (
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Linked Office Location</label>
                            <select value={form.officeLocationId} onChange={e => setForm({...form, officeLocationId: e.target.value})}
                                className={`w-full px-3 py-2 rounded-lg border text-sm ${isDark ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`}>
                                <option value="">None</option>
                                {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
                            </select>
                        </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Active</label>
                            <button onClick={() => setForm({...form, isActive: !form.isActive})}
                                className={`w-full py-2 rounded-lg text-sm font-medium ${form.isActive ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                                {form.isActive ? 'Active' : 'Inactive'}
                            </button>
                        </div>
                        <div>
                            <label className={`block text-xs font-medium mb-1 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>VPN</label>
                            <button onClick={() => setForm({...form, isVpn: !form.isVpn})}
                                className={`w-full py-2 rounded-lg text-sm font-medium ${form.isVpn ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'}`}>
                                {form.isVpn ? 'VPN' : 'Not VPN'}
                            </button>
                        </div>
                    </div>
                    {error && <p className="text-xs text-red-500">{error}</p>}
                </div>
                <div className="flex gap-3 mt-4 justify-end">
                    <button onClick={onClose} className={`px-4 py-2 rounded-lg text-sm ${isDark ? 'text-gray-300 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}>Cancel</button>
                    <button onClick={handleSave} disabled={saving || !form.name.trim() || !form.ipAddress.trim()}
                        className="px-4 py-2 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors">
                        {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ─── Verification Settings Tab ───────────────────────────────────────
const VerificationSettingsTab = ({ isDark }) => {
    const [settings, setSettings] = useState({ enabled: false, mode: 'warn', ipVerificationEnabled: true });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [saved, setSaved] = useState(false);

    useEffect(() => { loadSettings(); }, []);

    const loadSettings = async () => {
        setLoading(true);
        try {
            const res = await geoService.getGeoSettings();
            if (res.success) setSettings(res.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleSave = async () => {
        setSaving(true);
        setSaved(false);
        try {
            const res = await geoService.updateGeoSettings(settings);
            if (res.success) { setSettings(res.data); setSaved(true); setTimeout(() => setSaved(false), 2000); }
        } catch (err) { console.error(err); }
        finally { setSaving(false); }
    };

    if (loading) return <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-blue-500" /></div>;

    return (
        <div className="space-y-6 max-w-2xl">
            <div>
                <h2 className={`text-lg font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Verification Settings</h2>
                <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Configure how employee attendance location is verified.
                </p>
            </div>

            {/* Master Toggle */}
            <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                        {settings.enabled
                            ? <Shield className="w-6 h-6 text-green-500 mt-0.5 shrink-0" />
                            : <ShieldAlert className="w-6 h-6 text-gray-400 mt-0.5 shrink-0" />
                        }
                        <div>
                            <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                Geo Verification
                            </h3>
                            <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {settings.enabled
                                    ? 'Active — Employees must verify their location when punching in. Approved WFH requests are exempt.'
                                    : 'Disabled — No location verification. Employees punch in without any geo checks.'
                                }
                            </p>
                            {settings.enabled && (
                                <p className="text-xs mt-2 text-amber-600 dark:text-amber-400">
                                    Employees on desktop without GPS will be verified via their IP address against your configured office networks.
                                </p>
                            )}
                        </div>
                    </div>
                    <button onClick={() => setSettings(s => ({ ...s, enabled: !s.enabled }))}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.enabled ? 'bg-green-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'}`}>
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
                    </button>
                </div>
            </div>

            {/* Mode Selection (only shown when enabled) */}
            {settings.enabled && (
                <>
                    <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                        <h3 className={`font-semibold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`}>Unverified Punch Mode</h3>
                        <p className={`text-sm mb-4 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                            What happens when an employee's location cannot be verified (no GPS, IP not in office network, no approved WFH)?
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                            <button onClick={() => setSettings(s => ({ ...s, mode: 'warn' }))}
                                className={`p-4 rounded-xl border-2 text-left transition-colors ${
                                    settings.mode === 'warn'
                                        ? 'border-amber-500 bg-amber-50 dark:bg-amber-900/20'
                                        : isDark ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                                }`}>
                                <div className="flex items-center gap-2 mb-1">
                                    <AlertTriangle className={`w-4 h-4 ${settings.mode === 'warn' ? 'text-amber-500' : 'text-gray-400'}`} />
                                    <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Warn</span>
                                </div>
                                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Punches from unverified locations are allowed but flagged for admin review. Recommended for rollout.
                                </p>
                            </button>
                            <button onClick={() => setSettings(s => ({ ...s, mode: 'strict' }))}
                                className={`p-4 rounded-xl border-2 text-left transition-colors ${
                                    settings.mode === 'strict'
                                        ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                                        : isDark ? 'border-gray-700 hover:border-gray-600' : 'border-gray-200 hover:border-gray-300'
                                }`}>
                                <div className="flex items-center gap-2 mb-1">
                                    <ShieldAlert className={`w-4 h-4 ${settings.mode === 'strict' ? 'text-red-500' : 'text-gray-400'}`} />
                                    <span className={`font-medium text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>Strict</span>
                                </div>
                                <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Punches from unverified locations are rejected. Employees must be at an office or on approved WFH.
                                </p>
                            </button>
                        </div>
                    </div>

                    <div className={`rounded-xl border p-5 ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'}`}>
                        <div className="flex items-center justify-between">
                            <div>
                                <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>IP Verification Fallback</h3>
                                <p className={`text-sm mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                                    Use office network IP ranges to verify desktop users when GPS is unavailable.
                                </p>
                            </div>
                            <button onClick={() => setSettings(s => ({ ...s, ipVerificationEnabled: !s.ipVerificationEnabled }))}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings.ipVerificationEnabled ? 'bg-green-500' : isDark ? 'bg-gray-600' : 'bg-gray-300'}`}>
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings.ipVerificationEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                            </button>
                        </div>
                    </div>
                </>
            )}

            {/* Save Button */}
            <div className="flex items-center gap-3">
                <button onClick={handleSave} disabled={saving}
                    className="px-6 py-2.5 rounded-lg text-sm font-medium bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50 transition-colors">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin inline" /> : 'Save Settings'}
                </button>
                {saved && <span className="text-sm text-green-600 dark:text-green-400">Settings saved</span>}
            </div>
        </div>
    );
};

export default GeoLocation;
